import type { IFilter } from '@models/Filter'
import { endOfDay, startOfDay } from '@util/date-util'

import { apiClient } from '../../../gateways/api'
import type {
  SubprocessenSearch,
  SubprocessenSearchFilters,
} from '../types/SubprocessenSearch'

export { initialState, createFilters, searchSubprocessen }

const initialState: SubprocessenSearch = {
  data: { count: 0, skip: 0, limit: 25, items: [] },
  query: {
    filters: {
      gebied: [],
      type: [],
      assignedTo: [],
      labels: [],
      fromChangedDate: null,
      toChangedDate: null,
      fromDate: null,
      toDate: null,
      myList: false,
      startUitvoeringWeekTot: '',
      startUitvoeringWeekVan: '',
      gemeente: [],
      verantwoordelijke: null,
      typeWerk: [],
      processtatus: [],
      status: [],
      pauzeerReden: null,
      kpi: '',
    },
    sort: { sortBy: 'created.date', sortDirection: 'desc' },
    page: { skip: 0, limit: 25 },
  },
}

const createFilters = ({
  gebied,
  type,
  assignedTo,
  labels,
  toDate,
  fromDate,
  toChangedDate,
  fromChangedDate,
  myList,
  startUitvoeringWeekVan,
  startUitvoeringWeekTot,
  gemeente,
  verantwoordelijke,
  typeWerk,
  processtatus,
  status,
  pauzeerReden,
  kpi,
}: SubprocessenSearchFilters) => {
  const filters: IFilter[] = [{ name: 'isActive', value: true, operator: 'eq' }]

  if (status.length > 0) {
    filters.push({
      name: 'status',
      value: status.map((x) => x.code),
      operator: 'in',
    })
  } else {
    filters.push({
      name: 'isOpen',
      value: true,
      operator: 'eq',
    })
  }

  if (type && type.length > 0)
    filters.push({
      name: 'type',
      value: type.map(({ code }) => code),
      operator: 'in',
    })
  if (gebied && gebied.length > 0)
    filters.push({
      name: 'entity.gebiedscode',
      value: gebied.map(({ code }) => code),
      operator: 'in',
    })
  if (assignedTo && assignedTo.length > 0)
    filters.push({
      name: 'assignedTo',
      value: assignedTo.map(({ label }) => label),
      operator: 'in',
    })
  if (labels && labels.length > 0)
    filters.push({
      name: 'labels',
      value: labels.map(({ code }) => code),
      operator: 'in',
    })
  if (fromDate)
    filters.push({
      name: 'dueDate',
      value: startOfDay(fromDate),
      operator: 'gte',
    })
  if (toDate)
    filters.push({ name: 'dueDate', value: endOfDay(toDate), operator: 'lte' })
  if (fromChangedDate)
    filters.push({
      name: 'changed',
      value: startOfDay(fromChangedDate),
      operator: 'gte',
    })
  if (toChangedDate)
    filters.push({
      name: 'changed',
      value: endOfDay(toChangedDate),
      operator: 'lte',
    })

  if (startUitvoeringWeekVan)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekVan,
      operator: 'gte',
    })
  if (startUitvoeringWeekTot)
    filters.push({
      name: 'searchValues.startUitvoeringWeek',
      value: startUitvoeringWeekTot,
      operator: 'lte',
    })
  if (gemeente.length > 0)
    filters.push({
      name: 'searchValues.gemeente',
      value: gemeente,
      operator: 'in',
    })

  if (verantwoordelijke)
    filters.push({
      name: 'searchValues.verantwoordelijken',
      value: verantwoordelijke.code,
      operator: 'eq',
    })

  filters.push({
    name: 'entity.projecttype',
    value: ['Hoofdleiding'],
    operator: 'in',
  })
  if (myList) {
    filters.push({ name: 'my-list', value: true, operator: 'eq' })
  }
  if (Array.isArray(typeWerk) && typeWerk.length > 0) {
    filters.push({
      name: 'searchValues.typeWerk',
      operator: 'in',
      value: typeWerk,
    })
  }

  if (processtatus && processtatus.length > 0) {
    filters.push({
      name: 'searchValues.statusText',
      value: processtatus,
      operator: 'in',
    })
  }

  if (pauzeerReden) {
    filters.push({
      name: 'pauzeerReden',
      value: pauzeerReden.label,
      operator: 'eq',
    })
  }

  if (kpi) {
    filters.push({ name: 'kpi', value: kpi, operator: 'eq' })
  }

  return filters
}

const searchSubprocessen = async ({
  query,
}: {
  query: SubprocessenSearch['query']
}): Promise<SubprocessenSearch['data']> => {
  const { filters, page, sort } = query

  const { data } = await apiClient({
    url: '/rest/subproces/search',
    method: 'POST',
    data: { filters: createFilters(filters), ...page, ...sort },
  })

  return data
}
